import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  InputAdornment,
  Tooltip,
  Link
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Cloudinary } from '@cloudinary/url-gen';
import { AdvancedImage } from '@cloudinary/react';
import { fill } from '@cloudinary/url-gen/actions/resize';
import { autoGravity } from '@cloudinary/url-gen/qualifiers/gravity';

const News = () => {
  const CLOUDINARY_CLOUD_NAME = 'dhke5v8gz';
  const CLOUDINARY_UPLOAD_PRESET = 'avg_upload';

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentNews, setCurrentNews] = useState({
    title: '',
    content: '',
    summary: '',
    author: '',
    imageUrl: '',
    externalLink: '',
    hasExternalLink: false,
    linkLabel: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Charger les actualités depuis Firestore
  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const newsQuery = query(
        collection(db, 'news'),
        orderBy('createdAt', 'desc')
      );
      
      const newsSnapshot = await getDocs(newsQuery);
      
      if (newsSnapshot.empty) {
        setNews([]);
      } else {
        const newsData = newsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));
        setNews(newsData);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des actualités:', err);
      showSnackbar('Erreur lors du chargement des actualités', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem = null) => {
    if (newsItem) {
      setCurrentNews({
        ...newsItem,
        hasExternalLink: !!newsItem.externalLink,
        linkLabel: newsItem.linkLabel || 'En savoir plus'
      });
      setIsEditing(true);
    } else {
      setCurrentNews({
        title: '',
        content: '',
        summary: '',
        author: '',
        imageUrl: '',
        externalLink: '',
        hasExternalLink: false,
        linkLabel: 'En savoir plus'
      });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentNews(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setCurrentNews(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(currentNews.externalLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Vérifie si l'URL est valide
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Initialisation de Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: CLOUDINARY_CLOUD_NAME
    }
  });

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    try {
      setImageUploading(true);
      
      // Créer un objet FormData pour l'upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      formData.append('cloud_name', CLOUDINARY_CLOUD_NAME);
      
      // Upload vers Cloudinary
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur Cloudinary:', errorData);
        throw new Error(`Erreur d'upload: ${errorData.error?.message || 'Problème de connexion à Cloudinary'}`);
      }
      
      const data = await response.json();
      
      if (data.secure_url) {
        setCurrentNews(prev => ({
          ...prev,
          imageUrl: data.secure_url
        }));
        showSnackbar('Image téléchargée avec succès', 'success');
      } else {
        throw new Error('URL sécurisée non reçue de Cloudinary');
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement de l\'image:', error);
      showSnackbar(`Erreur lors du téléchargement de l'image: ${error.message}`, 'error');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSaveNews = async () => {
    try {
      // Validation des champs obligatoires
      if (!currentNews.title || !currentNews.content) {
        showSnackbar('Le titre et le contenu sont obligatoires', 'error');
        return;
      }

      // Validation du lien externe s'il est activé
      if (currentNews.hasExternalLink) {
        if (!currentNews.externalLink) {
          showSnackbar('Veuillez saisir un lien externe', 'error');
          return;
        }

        if (!isValidUrl(currentNews.externalLink)) {
          showSnackbar('Le lien externe n\'est pas valide. Assurez-vous qu\'il commence par http:// ou https://', 'error');
          return;
        }

        if (!currentNews.linkLabel) {
          showSnackbar('Veuillez saisir un libellé pour le lien', 'error');
          return;
        }
      }

      setLoading(true);
      
      let newsId = currentNews.id;
      
      // Si c'est une nouvelle actualité, générer un nouvel ID
      if (!isEditing) {
        newsId = doc(collection(db, 'news')).id;
      }
      
      // Préparer les données à sauvegarder
      const newsData = {
        ...currentNews,
        externalLink: currentNews.hasExternalLink ? currentNews.externalLink : '',
        linkLabel: currentNews.hasExternalLink ? currentNews.linkLabel : '',
        updatedAt: serverTimestamp(),
        createdAt: isEditing ? currentNews.createdAt : serverTimestamp()
      };
      
      // Supprimer les champs qui ne doivent pas être stockés dans Firestore
      delete newsData.id;
      delete newsData.hasExternalLink;
      
      // Sauvegarder dans Firestore
      await setDoc(doc(db, 'news', newsId), newsData, { merge: true });
      
      // Fermer le dialogue et rafraîchir la liste
      handleCloseDialog();
      fetchNews();
      
      showSnackbar(
        isEditing ? 'Actualité mise à jour avec succès' : 'Nouvelle actualité créée avec succès',
        'success'
      );
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'actualité:', error);
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'news', id));
      fetchNews();
      showSnackbar('Actualité supprimée avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'actualité:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Formater la date pour l'affichage
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Afficher une version tronquée d'URL longue
  const formatUrl = (url, maxLength = 30) => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + '/.../';
    } catch (e) {
      return url.substring(0, maxLength - 3) + '...';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#014527', fontWeight: 'bold' }}>
          Gestion des Actualités
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#01331d' } }}
        >
          Nouvelle Actualité
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress sx={{ color: '#014527' }} />
        </Box>
      ) : news.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Aucune actualité disponible
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Commencez par créer votre première actualité en cliquant sur le bouton "Nouvelle Actualité".
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#01331d' } }}
          >
            Créer une actualité
          </Button>
        </Paper>
      ) : (
        <>
          {/* Aperçu de la dernière actualité */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#014527' }}>
              Dernière actualité publiée
            </Typography>
            <Card sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              <CardMedia
                component="img"
                sx={{ width: { xs: '100%', md: 300 }, height: { xs: 200, md: 'auto' } }}
                image={news[0].imageUrl || `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/v1745764847/agrivision_placeholder_cklmxu.jpg`}
                alt={news[0].title}
              />
              <CardContent sx={{ flex: '1 0 auto', p: 3 }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {news[0].title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Publié le {formatDate(news[0].createdAt)} par {news[0].author || 'Agri Vision Gabon'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {news[0].summary || news[0].content.substring(0, 150) + '...'}
                </Typography>
                
                {news[0].externalLink && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      href={news[0].externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: '#014527', 
                        borderColor: '#014527',
                        '&:hover': { borderColor: '#01331d', bgcolor: 'rgba(1, 69, 39, 0.05)' }
                      }}
                    >
                      {news[0].linkLabel || 'En savoir plus'}
                    </Button>
                    <Tooltip title={news[0].externalLink} placement="top">
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Lien: {formatUrl(news[0].externalLink)}
                      </Typography>
                    </Tooltip>
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(news[0])}
                    sx={{ color: '#014527' }}
                  >
                    Modifier
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteNews(news[0].id)}
                    sx={{ color: 'error.main' }}
                  >
                    Supprimer
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Liste des actualités */}
          <Typography variant="h6" sx={{ mb: 2, color: '#014527' }}>
            Toutes les actualités
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Titre</TableCell>
                  <TableCell>Auteur</TableCell>
                  <TableCell>Date de publication</TableCell>
                  <TableCell>Lien externe</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {news.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.author || 'Non spécifié'}</TableCell>
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>
                      {item.externalLink ? (
                        <Tooltip title={item.externalLink}>
                          <a 
                            href={item.externalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: '#014527',
                                textDecoration: 'none'
                            }}
                            >
                            <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            {formatUrl(item.externalLink, 20)}
                            </a>
                        </Tooltip>
                      ) : (
                        'Non'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(item)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteNews(item.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Dialogue pour ajouter/modifier une actualité */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Modifier l\'actualité' : 'Ajouter une nouvelle actualité'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Titre"
                value={currentNews.title}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="author"
                label="Auteur"
                value={currentNews.author}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  disabled={imageUploading}
                  sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#01331d' } }}
                >
                  {imageUploading ? 'Téléchargement...' : 'Télécharger une image'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={uploadImage}
                  />
                </Button>
                {imageUploading && <CircularProgress size={24} />}
              </Box>
              {currentNews.imageUrl && (
                <Box sx={{ mt: 2 }}>
                  <img 
                    src={currentNews.imageUrl} 
                    alt="Aperçu" 
                    style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'cover' }} 
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="summary"
                label="Résumé (optionnel)"
                value={currentNews.summary}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="content"
                label="Contenu"
                value={currentNews.content}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={6}
                required
              />
            </Grid>

            {/* Section de lien externe */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, bgcolor: '#fafafa' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentNews.hasExternalLink}
                      onChange={handleSwitchChange}
                      name="hasExternalLink"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium', color: '#014527' }}>
                      Ajouter un lien externe
                    </Typography>
                  }
                />
                
                {currentNews.hasExternalLink && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      name="externalLink"
                      label="URL du lien externe"
                      value={currentNews.externalLink}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder="https://example.com/page"
                      required={currentNews.hasExternalLink}
                      helperText="Exemple: https://www.example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkIcon />
                          </InputAdornment>
                        ),
                        endAdornment: currentNews.externalLink && (
                          <InputAdornment position="end">
                            <Tooltip title={linkCopied ? "Copié!" : "Copier le lien"}>
                              <IconButton 
                                edge="end" 
                                onClick={handleLinkCopy}
                                size="small"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                      sx={{ mb: 2 }}
                    />
                    
                    <TextField
                      name="linkLabel"
                      label="Texte du bouton"
                      value={currentNews.linkLabel}
                      onChange={handleInputChange}
                      placeholder="En savoir plus"
                      required={currentNews.hasExternalLink}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <OpenInNewIcon />
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    {currentNews.externalLink && (
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'rgba(1, 69, 39, 0.05)', borderRadius: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          sx={{ 
                            color: '#014527', 
                            borderColor: '#014527',
                            '&:hover': { borderColor: '#01331d', bgcolor: 'rgba(1, 69, 39, 0.1)' }
                          }}
                        >
                          {currentNews.linkLabel || 'En savoir plus'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSaveNews} 
            variant="contained"
            disabled={loading || imageUploading}
            sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#01331d' } }}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default News;